/**
 * @file exportPickApi.ts
 * @brief B50: Export / Send only the parts you want (catalogue, live preview,
 *        Share as text, PDF), and how the file / text leaves the browser.
 *
 * @details
 * Contract: docs/product/meeting-export-b50.md (BACKEND B50 02168a9). Our
 * server sends no email: the PDF is downloaded (web) or saved / shared by the
 * Mac app, the text is copied.
 *
 * Mac: `verblizrDesktop.saveFile` / `shareFile` (DESKTOP B50-D) when the app
 * has them; an older Mac build or a browser falls back to a download, which
 * Electron already turns into a Save dialog.
 *
 * @date 02-11-2026 (B50-W)
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { desktopShell } from "@/shared/desktop/desktopShell";
import { toast } from "@/shared/hooks/useToast";
import { singleItemPick, type ExportCatalogue, type ExportItem, type PickBody } from "./components/meetingExportPick";

export type ExportRequest = {
  id: string;
  pick: PickBody;
  message?: string;
};

export type ExportPreviewSection = {
  key: string;
  label: string;
  total?: number;
  range?: { fromSecs: number; toSecs: number; from: string; to: string } | null;
  items: (ExportItem & { at?: string; speaker?: string })[];
};

export type ExportPreview = {
  preview: {
    title: string;
    date: string;
    what: string;
    message: { from: string; text: string } | null;
    sections: ExportPreviewSection[];
    fileName: string;
  };
  text: string;
  fileName: string;
  counts: Record<string, string>;
};

export type ExportTextResult = { text: string; what: string; counts: Record<string, string> };
export type ExportPdfResult = {
  pdfUrl: string;
  fileName: string;
  cached?: boolean;
  what: string;
  counts: Record<string, string>;
};

/** The date in the title / file name follows the sender's clock. */
function localTz(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function body({ pick, message }: Omit<ExportRequest, "id">) {
  const text = message?.trim();
  return { pick, ...(text ? { message: text } : {}), tz: localTz() };
}

export const exportPickKeys = {
  catalogue: (id: string) => ["company", "meetings", "export-pick", id] as const,
};

/** GET /export — fresh every time the sheet opens (ids belong to the meeting as it stands). */
export function useMeetingExportCatalogue(id: string, enabled: boolean) {
  return useQuery({
    queryKey: exportPickKeys.catalogue(id),
    queryFn: async () => {
      const raw = await apiGet<ExportCatalogue & { success?: boolean }>(boardroomEndpoints.exportPick(id));
      return raw;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Debounced by the sheet. Logs nothing, saves nothing. */
export function useMeetingExportPreview(id: string, req: Omit<ExportRequest, "id"> | null) {
  return useQuery({
    queryKey: ["company", "meetings", "export-preview", id, req] as const,
    queryFn: () => apiPost<ExportPreview>(boardroomEndpoints.exportPickPreview(id), body(req!)),
    enabled: !!req,
    staleTime: 30_000,
    retry: false,
    placeholderData: (prev) => prev,
  });
}

export function useMeetingExportText() {
  return useMutation<ExportTextResult, Error, ExportRequest>({
    mutationFn: ({ id, ...rest }) => apiPost<ExportTextResult>(boardroomEndpoints.exportPickText(id), body(rest)),
  });
}

export function useMeetingExportPdf() {
  return useMutation<ExportPdfResult, Error, ExportRequest>({
    mutationFn: ({ id, ...rest }) =>
      apiPost<ExportPdfResult>(boardroomEndpoints.exportPickPdf(id), body(rest), {
        // One Chromium render; Full minutes may also build its cached PDF.
        timeout: 180_000,
      }),
  });
}

/**
 * Copy text that the server is still making. Called inside the click, so
 * Safari keeps the click's permission: a ClipboardItem takes the promise.
 * Elsewhere (or if that fails) the text is copied once it arrives.
 */
export async function copyTextFrom(make: Promise<string>): Promise<string> {
  const Item = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
  if (Item && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new Item({ "text/plain": make.then((t) => new Blob([t], { type: "text/plain" })) }),
      ]);
      return await make;
    } catch {
      // fall through to the plain copy
    }
  }
  const text = await make;
  await copyText(text);
  return text;
}

/** Copy, with the old textarea path for a page without clipboard access. */
export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // fall through
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  ta.remove();
  if (!ok) throw new Error("Copy is blocked in this browser.");
}

export type PdfDelivery = "saved" | "shared" | "downloaded" | "opened" | "cancelled";

/**
 * Save the PDF under `fileName`. Mac app with the B50-D bridge: its own Save
 * dialog. Else fetch the signed URL as a blob and download it under the name
 * (the URL is cross-origin, so `<a download>` alone would keep the URL's
 * name); if the storage host refuses the fetch, open it in a new tab.
 */
export async function savePdf(file: { url: string; fileName: string }): Promise<PdfDelivery> {
  const shell = desktopShell();
  if (shell?.saveFile) {
    const res = await shell.saveFile(file);
    if (res.ok) return "saved";
    if (res.error === "CANCELLED") return "cancelled";
    // Anything else: the plain download below.
  }
  try {
    const res = await fetch(file.url);
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = file.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
    return "downloaded";
  } catch {
    window.open(file.url, "_blank", "noopener");
    return "opened";
  }
}

/** The Mac's Share menu for the PDF (B50-D); null = not on this build. */
export function canShareFileNatively(): boolean {
  return typeof desktopShell()?.shareFile === "function";
}

export async function sharePdfNatively(file: { url: string; fileName: string }): Promise<PdfDelivery> {
  const shell = desktopShell();
  if (!shell?.shareFile) return savePdf(file);
  const res = await shell.shareFile(file);
  if (res.ok) return "shared";
  if (res.error === "CANCELLED") return "cancelled";
  return savePdf(file);
}

/**
 * The quick path (DES 02-11-2026: "often it is just one person's to-do and
 * one decision"): one to-do (its id) or decision (its index in
 * key_decisions) copied as text, via the same /export/text route.
 */
export function useShareOneItem(meetingId: string) {
  const exportText = useMeetingExportText();
  return {
    pending: exportText.isPending,
    share: async (kind: "todos" | "decisions", id: string) => {
      try {
        await copyTextFrom(
          exportText.mutateAsync({ id: meetingId, pick: singleItemPick(kind, id) }).then((r) => r.text),
        );
        toast({
          variant: "success",
          title: kind === "todos" ? "To-do copied" : "Decision copied",
          description: "Paste it into an email or a chat.",
        });
      } catch (err) {
        toast({ variant: "destructive", title: "Could not copy", description: err instanceof Error ? err.message : "Please try again." });
      }
    },
  };
}
